import GraphRanges from "@/mixins/GraphRanges";
import { Patient } from "@/store/modules/patient/types";
import { mixins } from "vue-class-component";
import { Component } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { MultiChartConfig } from "../shared/chart/MultiChartWithSharedRange";

const patientNamespace = { namespace: "patient" };
@Component({})
export default class Pump extends mixins(GraphRanges) {
  @Getter("graph", patientNamespace)
  graph!: any;

  @Getter("current", patientNamespace)
  current!: Patient;

  get pumps() {
    const legend = this.current.body.atoms.pump.legend;

    try {
      const pumps = JSON.parse(legend);
      return pumps;
    } catch (e) {
      return [];
    }
  }

  get ecmoCharts(): MultiChartConfig[] {
    const chartList: MultiChartConfig[] = [];

    chartList.push({
      title: "ECMO Flow (L/min) & Resistance (L/min/mmHg)",
      data: this.graph(
        [
          { key: "MCS.ECMO.Flow", label: "Flow" },
          { key: "MCS.ECMO.Resistance", label: "Resistance" }
        ],
        true
      ),
      suggestedMax: 10
    });

    const so2titles = [];
    if (this.graph("MCS.ECMO.SO2In", true).length) {
      so2titles.push("SO<sub>2</sub>In");
    }
    if (this.graph("MCS.ECMO.SO2Out", true).length) {
      so2titles.push("SO<sub>2</sub>Out");
    }

    chartList.push({
      title: so2titles.join(" & ") + " (%)",
      data: this.graph(
        [
          { key: "MCS.ECMO.SO2In", label: "SO2In" },
          { key: "MCS.ECMO.SO2Out", label: "SO2Out" },
          { key: "MCS.ECMO.SO2Delta", label: "SO2Delta" }
        ],
        true
      ),
      suggestedMax: 100
    });

    chartList.push({
      title: "Oxygenator Pressure (mmHg)",
      data: this.graph(
        [
          { key: "MCS.ECMO.PInt", label: "PInt" },
          { key: "MCS.ECMO.PArt", label: "PArt" },
          { key: "MCS.ECMO.PDelta", label: "PDelta" }
        ],
        true
      )
    });

    return chartList;
  }

  get lvadCharts(): MultiChartConfig[] {
    const chartList: MultiChartConfig[] = [];

    chartList.push({
      title: "VAD Flow and Power",
      data: this.graph(
        [
          { key: "MCS.LVAD.Flow", label: "Flow" },
          { key: "MCS.LVAD.Power", label: "Power" }
        ],
        true
      ),
      suggestedMax: 10
    });

    chartList.push({
      title: "VAD RPM",
      data: this.graph([{ key: "MCS.LVAD.RPM", label: "RPM" }], true),
      suggestedMax: 10000
    });

    return chartList;
  }

  get rvadCharts(): MultiChartConfig[] {
    const chartList: MultiChartConfig[] = [];

    chartList.push({
      title: "RVAD Flow and Power",
      data: this.graph(
        [
          { key: "MCS.RVAD.Flow", label: "Flow" },
          { key: "MCS.RVAD.Power", label: "Power" }
        ],
        true
      ),
      suggestedMax: 10
    });

    chartList.push({
      title: "RVAD RPM",
      data: this.graph([{ key: "MCS.RVAD.RPM", label: "RPM" }], true),
      suggestedMax: 10000
    });

    return chartList;
  }

  get bivadCharts(): MultiChartConfig[] {
    const chartList: MultiChartConfig[] = [];

    chartList.push({
      title: "BiVAD Flow and Power",
      data: this.graph(
        [
          { key: "MCS.BiVAD.Flow", label: "Flow" },
          { key: "MCS.BiVAD.Power", label: "Power" }
        ],
        true
      ),
      suggestedMax: 10
    });

    chartList.push({
      title: "BiVAD RPM",
      data: this.graph([{ key: "MCS.BiVAD.RPM", label: "RPM" }], true),
      suggestedMax: 10000
    });

    return chartList;
  }

  parsePumpData(data: any) {
    return data.map((item: any) => {
      let [name, value] = item.legend.split("|");
      return { name, value };
    });
  }

  get isMobileThin() {
    return (
      this.$vuetify.breakpoint.smAndDown && this.$vuetify.breakpoint.width < 600
    );
  }

  parsePumpData2(data: any) {
    const list = data
      .map((item: any) => {
        let [name, value] = item.legend.split("|");
        name = name.replace("O2", "O<sub>2</sub>");
        value = value
          .replace(" seconds", "&nbsp;seconds")
          .replace(" minutes", "&nbsp;minutes")
          .replace(" hours", "&nbsp;hours")
          .replace(" days", "&nbsp;days");
        if (this.isMobileThin) {
          return [{ name, value }];
        } else {
          return { name, value };
        }
      })
      .filter((data: any) => {
        const name = this.isMobileThin ? data[0].name : data.name;
        return name !== "Mode";
      });

    if (this.isMobileThin) {
      return list;
    } else {
      let result: any[] = [];
      const halfway = Math.ceil(list.length / 2);
      for (let i = 0; i < halfway; i++) {
        const second =
          i + halfway < list.length
            ? list[i + halfway]
            : { name: "", value: "" };
        result.push([list[i], second]);
      }
      return result;
    }
  }
}
